/**
 * Session Cache Utility
 *
 * Uses a stale-while-revalidate strategy so that intra-app navigations
 * are never blocked by a network round-trip to Better Auth's get-session endpoint.
 *
 * Timing thresholds:
 *  ┌──────────────────────────────────────────────────────────────┐
 *  │  0 – STALE_TTL (30 s)  →  return cache, no network call    │
 *  │  STALE_TTL – MAX_TTL   →  return cache, revalidate in bg   │
 *  │  > MAX_TTL (5 min)     →  block until fresh fetch completes │
 *  │  No cache at all       →  block until first fetch completes │
 *  └──────────────────────────────────────────────────────────────┘
 *
 * Note: This module is client-only. On SSR (import.meta.server), call
 * authClient.getSession() directly — do not use this cache.
 */

// Minimal shape of the Better Auth session result used across the app.
// The `user` field is accessed in global-auth middleware for PostHog identification.
type SessionUser = { id: string; email: string; name: string; image?: string | null };
type SessionResult = { data: { user?: SessionUser | null } | null } | null;

/** Minimal auth client shape required by getCachedSession. */
export type AuthClientWithGetSession = {
  getSession: () => Promise<SessionResult>;
};

let pendingSession: Promise<SessionResult> | null = null;
let cachedSession: SessionResult = null;
let cacheTimestamp = 0;

/** Below this age the cache is considered fresh — no network call at all */
export const STALE_TTL = 30_000; // 30 seconds

/** Above this age the cache is too old to trust — must wait for a fresh fetch */
export const MAX_TTL = 5 * 60_000; // 5 minutes

/**
 * Get the auth session with stale-while-revalidate caching.
 *
 * - Fresh (< STALE_TTL): instant, no network call.
 * - Stale (STALE_TTL – MAX_TTL): instant return + background refresh.
 * - Expired (> MAX_TTL) or empty: blocks until fresh session is fetched.
 * - Concurrent calls are coalesced into a single in-flight request.
 *
 * @throws Error if called on the server side.
 */
export async function getCachedSession(
  authClient: AuthClientWithGetSession,
): Promise<SessionResult> {
  if (typeof import.meta !== 'undefined' && import.meta.server) {
    throw new Error(
      'sessionCache is client-only — use getSession() directly on the server',
    );
  }

  const now = Date.now();
  const age = now - cacheTimestamp;

  // ── Fresh cache: return immediately ──
  if (cachedSession !== null && age < STALE_TTL) {
    return cachedSession;
  }

  // ── Stale but usable: return immediately, revalidate in background ──
  if (cachedSession !== null && age < MAX_TTL) {
    if (!pendingSession) {
      pendingSession = authClient
        .getSession()
        .then((result) => {
          cachedSession = result;
          cacheTimestamp = Date.now();
          pendingSession = null;
          return result;
        })
        .catch(() => {
          // Background refresh failed — keep serving stale cache
          pendingSession = null;
          return cachedSession;
        });
    }
    return cachedSession;
  }

  // ── Expired or no cache: must wait for a fresh result ──
  if (pendingSession) {
    return pendingSession;
  }

  pendingSession = authClient
    .getSession()
    .then((result) => {
      cachedSession = result;
      cacheTimestamp = Date.now();
      pendingSession = null;
      return result;
    })
    .catch((err) => {
      pendingSession = null;
      throw err;
    });

  return pendingSession;
}

/**
 * Invalidate the session cache (e.g., on logout or login).
 */
export function clearSessionCache() {
  cachedSession = null;
  cacheTimestamp = 0;
  pendingSession = null;
}

/**
 * Directly prime the session cache with a known-good result.
 *
 * Use this immediately after a successful signup/login to guarantee the
 * next getCachedSession call (e.g. in the global middleware) gets an
 * instant cache hit instead of racing against a cold or stale fetch.
 */
export function primeSessionCache(session: SessionResult): void {
  cachedSession = session;
  cacheTimestamp = Date.now();
  pendingSession = null;
}
